import { useState, useEffect, useCallback, useMemo, Fragment } from 'react';
import {
  Box, Typography, Button, Card, CardContent, Stack, Chip, Divider,
  IconButton, CircularProgress, Table, Sheet, Select, Option,
} from '@mui/joy';
import { Add, Edit, Delete, ArrowBack, PersonAdd, ArrowUpward, ArrowDownward, UnfoldMore, Lock, KeyboardArrowDown, KeyboardArrowRight } from '@mui/icons-material';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { projectService } from '@/services/projectService';
import {
  ProjectWithTasks, TaskWithAssignees,
  CreateTaskRequest, UpdateTaskRequest, TaskStatus, TASK_STATUS_LABELS, TASK_PRIORITY_LABELS,
} from '@/types/project';
import ProjectStatusChip from '@/components/ProjectStatusChip';
import TaskStatusChip from '@/components/TaskStatusChip';
import TaskPriorityChip from '@/components/TaskPriorityChip';
import ProjectForm from '@/components/ProjectForm';
import TaskForm from '@/components/TaskForm';
import TaskAssignmentModal from '@/components/TaskAssignmentModal';
import toast from 'react-hot-toast';

export default function ProjectDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { permissions } = useAuth();
  const [project, setProject] = useState<ProjectWithTasks | null>(null);
  const [loading, setLoading] = useState(true);
  const [editProjectOpen, setEditProjectOpen] = useState(false);
  const [taskFormOpen, setTaskFormOpen] = useState(false);
  const [editingTask, setEditingTask] = useState<TaskWithAssignees | null>(null);
  const [assignTask, setAssignTask] = useState<TaskWithAssignees | null>(null);

  type SortField = 'name' | 'status' | 'priority' | 'due_date' | 'shift_type' | 'assignees';
  type SortDir = 'asc' | 'desc';
  const [sortField, setSortField] = useState<SortField>('name');
  const [sortDir, setSortDir] = useState<SortDir>('asc');
  const [expandedTasks, setExpandedTasks] = useState<Set<number>>(new Set());

  const toggleExpanded = (taskId: number) => {
    setExpandedTasks(prev => {
      const next = new Set(prev);
      if (next.has(taskId)) next.delete(taskId);
      else next.add(taskId);
      return next;
    });
  };

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDir('asc');
    }
  };

  const canEdit = permissions.includes('projects.edit');
  const canCreate = permissions.includes('projects.create');
  const canDelete = permissions.includes('projects.delete');
  const canAssign = permissions.includes('projects.assign');
  const canUpdateStatus = permissions.includes('projects.update_task_status');

  const load = useCallback(async (silent = false) => {
    if (!id) return;
    if (!silent) setLoading(true);
    try {
      const data = await projectService.getProject(parseInt(id));
      setProject(data);
      // Keep the assignment modal in sync without closing/re-opening it
      if (silent) {
        setAssignTask(prev => prev ? (data.tasks.find(t => t.id === prev.id) ?? prev) : null);
      }
    } catch {
      if (!silent) {
        toast.error('Failed to load project');
        navigate('/projects');
      }
    } finally {
      if (!silent) setLoading(false);
    }
  }, [id, navigate]);

  useEffect(() => { load(); }, [load]);

  const handleUpdateProject = async (data: any) => {
    if (!project) return;
    await projectService.updateProject(project.id, data);
    toast.success('Project updated');
    load();
  };

  const handleCreateTask = async (data: CreateTaskRequest | UpdateTaskRequest) => {
    await projectService.createTask(data as CreateTaskRequest);
    toast.success('Task created');
    load();
  };

  const handleUpdateTask = async (data: CreateTaskRequest | UpdateTaskRequest) => {
    if (!editingTask) return;
    await projectService.updateTask(editingTask.id, data as UpdateTaskRequest);
    toast.success('Task updated');
    setEditingTask(null);
    load();
  };

  const handleUpdateTaskStatus = async (taskId: number, status: TaskStatus) => {
    try {
      await projectService.updateTaskStatus(taskId, status);
      toast.success('Status updated');
      load();
    } catch (err: any) {
      toast.error(err.response?.data?.error || 'Failed to update status');
    }
  };

  const handleDeleteTask = async (taskId: number) => {
    if (!window.confirm('Delete this task?')) return;
    try {
      await projectService.deleteTask(taskId);
      toast.success('Task deleted');
      load();
    } catch {
      toast.error('Failed to delete task');
    }
  };

  const handleAssign = async (employeeId: number) => {
    if (!assignTask) return;
    try {
      await projectService.assignEmployee(assignTask.id, employeeId);
      toast.success('Employee assigned');
      load(true);
    } catch (err: any) {
      toast.error(err.response?.data?.error || 'Assignment failed');
    }
  };

  const handleUnassign = async (employeeId: number) => {
    if (!assignTask) return;
    try {
      await projectService.unassignEmployee(assignTask.id, employeeId);
      toast.success('Employee unassigned');
      load(true);
    } catch (err: any) {
      toast.error(err.response?.data?.error || 'Failed to unassign employee');
    }
  };

  const PRIORITY_ORDER: Record<string, number> = { low: 0, medium: 1, high: 2 };
  const STATUS_ORDER: Record<string, number> = { not_started: 0, in_progress: 1, on_hold: 2, completed: 3, cancelled: 4 };

  const sortedTasks = useMemo(() => {
    if (!project) return [];
    return [...project.tasks].sort((a, b) => {
      let cmp = 0;
      switch (sortField) {
        case 'name':
          cmp = a.name.localeCompare(b.name);
          break;
        case 'status':
          cmp = (STATUS_ORDER[a.status] ?? 0) - (STATUS_ORDER[b.status] ?? 0);
          break;
        case 'priority':
          cmp = (PRIORITY_ORDER[a.priority] ?? 0) - (PRIORITY_ORDER[b.priority] ?? 0);
          break;
        case 'due_date':
          cmp = (a.due_date ?? '').localeCompare(b.due_date ?? '');
          break;
        case 'shift_type':
          cmp = (a.shift_type?.name ?? '').localeCompare(b.shift_type?.name ?? '');
          break;
        case 'assignees':
          cmp = a.assignees.length - b.assignees.length;
          break;
      }
      return sortDir === 'asc' ? cmp : -cmp;
    });
  }, [project, sortField, sortDir]);

  const formatDate = (d: string | null) => d ? new Date(d).toLocaleDateString() : '—';

  if (loading) {
    return <Box sx={{ display: 'flex', justifyContent: 'center', py: 10 }}><CircularProgress /></Box>;
  }

  if (!project) return null;

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2 }}>
        <IconButton variant="plain" onClick={() => navigate('/projects')}><ArrowBack /></IconButton>
        <Typography level="h3" sx={{ flex: 1 }}>{project.name}</Typography>
        {project.is_private && <Chip size="sm" variant="soft" color="neutral" startDecorator={<Lock sx={{ fontSize: 14 }} />}>Private</Chip>}
        <ProjectStatusChip status={project.status} />
        {canEdit && (
          <Button size="sm" variant="outlined" startDecorator={<Edit />} onClick={() => setEditProjectOpen(true)}>
            Edit
          </Button>
        )}
      </Stack>

      {/* Project details */}
      <Card variant="outlined" sx={{ mb: 3 }}>
        <CardContent>
          <Stack spacing={1}>
            {project.description && <Typography level="body-md">{project.description}</Typography>}
            <Stack direction="row" spacing={3}>
              <Typography level="body-sm">Start: {formatDate(project.start_date)}</Typography>
              <Typography level="body-sm">End: {formatDate(project.end_date)}</Typography>
              <Typography level="body-sm">
                Created by: {project.created_by_user.first_name} {project.created_by_user.last_name}
              </Typography>
            </Stack>
          </Stack>
        </CardContent>
      </Card>

      {/* Tasks section */}
      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
        <Typography level="h4">Tasks ({project.tasks.length})</Typography>
        {canCreate && (
          <Button size="sm" startDecorator={<Add />} onClick={() => { setEditingTask(null); setTaskFormOpen(true); }}>
            Add Task
          </Button>
        )}
      </Stack>

      {project.tasks.length === 0 ? (
        <Typography level="body-md" sx={{ textAlign: 'center', py: 4 }}>No tasks yet.</Typography>
      ) : (
        <Sheet variant="outlined" sx={{ borderRadius: 'sm', overflow: 'auto' }}>
          <Table stickyHeader hoverRow>
            <thead>
              <tr>
                <th style={{ width: 40 }} aria-label="Expand" />
                {(
                  [
                    { field: 'name', label: 'Name', width: undefined },
                    { field: 'status', label: 'Status', width: 145 },
                    { field: 'priority', label: 'Priority', width: 110 },
                    { field: 'due_date', label: 'Due Date', width: 110 },
                    { field: 'shift_type', label: 'Shift Type', width: 140 },
                    { field: 'assignees', label: 'Assignees', width: undefined },
                  ] as { field: SortField; label: string; width?: number }[]
                ).map(({ field, label, width }) => (
                  <th
                    key={field}
                    style={{ width, cursor: 'pointer', userSelect: 'none', whiteSpace: 'nowrap' }}
                    onClick={() => handleSort(field)}
                  >
                    <Stack direction="row" alignItems="center" spacing={0.5} component="span">
                      <span>{label}</span>
                      {sortField === field
                        ? sortDir === 'asc'
                          ? <ArrowUpward sx={{ fontSize: 14 }} />
                          : <ArrowDownward sx={{ fontSize: 14 }} />
                        : <UnfoldMore sx={{ fontSize: 14, opacity: 0.35 }} />}
                    </Stack>
                  </th>
                ))}
                <th style={{ width: 140 }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {sortedTasks.map(task => {
                const isExpanded = expandedTasks.has(task.id);
                const hasDescription = !!task.description;
                return (
                <Fragment key={task.id}>
                <tr>
                  <td>
                    {hasDescription ? (
                      <IconButton
                        size="sm"
                        variant="plain"
                        onClick={() => toggleExpanded(task.id)}
                        aria-label={isExpanded ? 'Collapse description' : 'Expand description'}
                        aria-expanded={isExpanded}
                      >
                        {isExpanded ? <KeyboardArrowDown /> : <KeyboardArrowRight />}
                      </IconButton>
                    ) : null}
                  </td>
                  <td>
                    <Stack direction="row" spacing={0.5} alignItems="center">
                      <Typography level="body-sm" fontWeight="md">{task.name}</Typography>
                      {task.is_private && <Lock sx={{ fontSize: 14, color: 'text.tertiary' }} />}
                    </Stack>
                    {task.description && !isExpanded && (
                      <Typography
                        level="body-xs"
                        color="neutral"
                        sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: 260, cursor: 'pointer' }}
                        onClick={() => toggleExpanded(task.id)}
                      >
                        {task.description}
                      </Typography>
                    )}
                  </td>
                  <td>
                    {canUpdateStatus ? (
                      <Select
                        size="sm"
                        value={task.status}
                        onChange={(_, v) => v && handleUpdateTaskStatus(task.id, v)}
                        sx={{ width: '100%' }}
                      >
                        {(Object.keys(TASK_STATUS_LABELS) as TaskStatus[]).map(s => (
                          <Option key={s} value={s}>{TASK_STATUS_LABELS[s]}</Option>
                        ))}
                      </Select>
                    ) : (
                      <TaskStatusChip status={task.status} />
                    )}
                  </td>
                  <td><TaskPriorityChip priority={task.priority} /></td>
                  <td><Typography level="body-xs">{formatDate(task.due_date)}</Typography></td>
                  <td>
                    {task.shift_type ? (
                      <Chip size="sm" variant="soft" sx={{ bgcolor: task.shift_type.color + '22', color: task.shift_type.color }}>
                        {task.shift_type.name}
                      </Chip>
                    ) : '—'}
                  </td>
                  <td>
                    <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
                      {task.assignees.map(a => (
                        <Chip key={a.id} size="sm" variant="outlined">{a.first_name} {a.last_name}</Chip>
                      ))}
                      {task.assignees.length === 0 && <Typography level="body-xs" color="neutral">Unassigned</Typography>}
                    </Stack>
                  </td>
                  <td>
                    <Stack direction="row" spacing={0.5}>
                      {canAssign && (
                        <IconButton size="sm" variant="plain" onClick={() => setAssignTask(task)}>
                          <PersonAdd />
                        </IconButton>
                      )}
                      {canEdit && (
                        <IconButton size="sm" variant="plain" onClick={() => { setEditingTask(task); setTaskFormOpen(true); }}>
                          <Edit />
                        </IconButton>
                      )}
                      {canDelete && (
                        <IconButton size="sm" variant="plain" color="danger" onClick={() => handleDeleteTask(task.id)}>
                          <Delete />
                        </IconButton>
                      )}
                    </Stack>
                  </td>
                </tr>
                {isExpanded && hasDescription && (
                  <tr>
                    <td />
                    <td colSpan={7} style={{ background: 'var(--joy-palette-neutral-50, #f7f7f8)' }}>
                      <Typography
                        level="body-sm"
                        color="neutral"
                        sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word', py: 1 }}
                      >
                        {task.description}
                      </Typography>
                    </td>
                  </tr>
                )}
                </Fragment>
                );
              })}
            </tbody>
          </Table>
        </Sheet>
      )}

      {/* Modals */}
      <ProjectForm
        open={editProjectOpen}
        onClose={() => setEditProjectOpen(false)}
        onSubmit={handleUpdateProject}
        project={project}
      />
      <TaskForm
        open={taskFormOpen}
        onClose={() => { setTaskFormOpen(false); setEditingTask(null); }}
        onSubmit={editingTask ? handleUpdateTask : handleCreateTask}
        task={editingTask}
        projectId={project.id}
      />
      {assignTask && (
        <TaskAssignmentModal
          open={!!assignTask}
          onClose={() => { setAssignTask(null); load(); }}
          task={assignTask}
          onAssign={handleAssign}
          onUnassign={handleUnassign}
        />
      )}
    </Box>
  );
}
